class AutoRefillBuyBlock extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.isBusy = false;
    this.variants = this.parseMainVariants();
    this.currentVariantInput = this.querySelector('[data-auto-refill-current-variant-id]');
    this.planCards = Array.from(this.querySelectorAll('[data-auto-refill-plan]'));
    this.frequencySelect = this.querySelector('[data-auto-refill-frequency-select]');
    this.subscriptionSummary = this.querySelector('[data-auto-refill-summary-subscription]');
    this.subscriptionPrice = this.querySelector('[data-auto-refill-subscription-price]');
    this.subscriptionCurrentPrice = this.querySelector('[data-auto-refill-subscription-current-price]');
    this.subscriptionCompare = this.querySelector('[data-auto-refill-subscription-compare]');
    this.subscriptionDescription = this.querySelector('[data-auto-refill-subscription-description]');
    this.oncePrice = this.querySelector('[data-auto-refill-once-price]');
    this.onceCompare = this.querySelector('[data-auto-refill-once-compare]');
    this.saveTag = this.querySelector('[data-auto-refill-save]');
    this.summaryToday = this.querySelector('[data-auto-refill-summary-today]');
    this.summaryFutureLabel = this.querySelector('[data-auto-refill-summary-future-label]');
    this.summaryRefill = this.querySelector('[data-auto-refill-summary-refill]');
    this.ctaButton = this.querySelector('[data-auto-refill-cta]');
    this.ctaText = this.querySelector('[data-auto-refill-cta-text]');
    this.errorRegion = this.querySelector('[data-auto-refill-error]');
    this.pdpRoot = this.closest('pdp-main-product, pdp-main-product-new-page');
    this.stickyButton = this.pdpRoot?.querySelector('[data-pdp-sticky-add-to-cart]') || null;
    this.controlsStickyButton = Boolean(this.stickyButton && !this.pdpRoot?.querySelector('[data-buy-buttons-block]'));
    this.selectedPlan = this.dataset.initialPlan === 'once' ? 'once' : 'subscription';
    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();

    this.onPlanClick = this.handlePlanClick.bind(this);
    this.onPlanKeydown = this.handlePlanKeydown.bind(this);
    this.onFrequencyClick = this.handleFrequencyClick.bind(this);
    this.onFrequencyChange = this.handleFrequencyChange.bind(this);
    this.onCtaClick = this.handleCtaClick.bind(this);
    this.onVariantInputChange = this.handleVariantInputChange.bind(this);
    this.onStickyClick = this.handleStickyClick.bind(this);

    this.planCards.forEach((card) => {
      card.addEventListener('click', this.onPlanClick);
      card.addEventListener('keydown', this.onPlanKeydown);
    });
    this.frequencySelect?.addEventListener('click', this.onFrequencyClick);
    this.frequencySelect?.addEventListener('change', this.onFrequencyChange);
    this.currentVariantInput?.addEventListener('change', this.onVariantInputChange);
    this.ctaButton?.addEventListener('click', this.onCtaClick);

    if (this.controlsStickyButton) {
      this.stickyButton.addEventListener('click', this.onStickyClick, { capture: true });
    }

    this.syncUI();
  }

  disconnectedCallback() {
    this.planCards?.forEach((card) => {
      card.removeEventListener('click', this.onPlanClick);
      card.removeEventListener('keydown', this.onPlanKeydown);
    });
    this.frequencySelect?.removeEventListener('click', this.onFrequencyClick);
    this.frequencySelect?.removeEventListener('change', this.onFrequencyChange);
    this.currentVariantInput?.removeEventListener('change', this.onVariantInputChange);
    this.ctaButton?.removeEventListener('click', this.onCtaClick);

    if (this.controlsStickyButton && this.stickyButton) {
      this.stickyButton.removeEventListener('click', this.onStickyClick, { capture: true });
    }
  }

  parseMainVariants() {
    const script = this.querySelector('[data-auto-refill-main-variants-json]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  getDatasetVariant() {
    const id = Number.parseInt(this.dataset.mainVariantId || '', 10);
    const price = Number.parseInt(this.dataset.mainPriceCents || '0', 10);
    const compareAtPrice = Number.parseInt(this.dataset.mainCompareCents || '0', 10);

    return {
      id,
      available: this.dataset.mainAvailable === 'true',
      price: Number.isNaN(price) ? 0 : price,
      compare_at_price: Number.isNaN(compareAtPrice) ? 0 : compareAtPrice,
    };
  }

  getCurrentVariantId() {
    const inputId = Number.parseInt(this.currentVariantInput?.value || '', 10);
    if (!Number.isNaN(inputId)) return inputId;

    const rootId = Number.parseInt(this.pdpRoot?.currentVariantId || '', 10);
    if (!Number.isNaN(rootId)) return rootId;

    const datasetId = Number.parseInt(this.dataset.mainVariantId || '', 10);
    return Number.isNaN(datasetId) ? null : datasetId;
  }

  getVariantById(variantId) {
    if (!variantId) return null;
    return this.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  handleVariantInputChange() {
    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();
    this.syncUI();
  }

  handlePlanClick(event) {
    const card = event.currentTarget;
    if (!(card instanceof HTMLElement)) return;
    if (card.getAttribute('aria-disabled') === 'true') return;

    this.selectPlan(card.dataset.autoRefillPlan === 'once' ? 'once' : 'subscription');
  }

  handlePlanKeydown(event) {
    if (event.target instanceof HTMLElement && event.target.closest('[data-auto-refill-frequency-select]')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.handlePlanClick(event);
  }

  handleFrequencyClick(event) {
    event.stopPropagation();
    this.selectPlan('subscription');
  }

  handleFrequencyChange() {
    this.selectPlan('subscription');
  }

  handleCtaClick(event) {
    event.preventDefault();
    this.addSelectionToCart({ redirectToCheckout: false });
  }

  handleStickyClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addSelectionToCart({ redirectToCheckout: false });
  }

  selectPlan(plan) {
    if (plan === 'subscription' && !this.hasSubscriptionOptions()) return;

    this.selectedPlan = plan === 'once' ? 'once' : 'subscription';
    this.clearError();
    this.syncUI();
  }

  hasSubscriptionOptions() {
    return Boolean(this.frequencySelect?.options?.length && this.getMainSellingPlanId());
  }

  getSelectedFrequencyOption() {
    if (!(this.frequencySelect instanceof HTMLSelectElement)) return null;
    return this.frequencySelect.selectedOptions?.[0] || this.frequencySelect.options[this.frequencySelect.selectedIndex] || null;
  }

  getSelectedSellingPlanId() {
    const option = this.getSelectedFrequencyOption();
    const sellingPlanId = Number.parseInt(option?.dataset.sellingPlanId || option?.value || '', 10);
    return Number.isNaN(sellingPlanId) ? null : sellingPlanId;
  }

  isSubscriptionSelected() {
    return this.selectedPlan === 'subscription';
  }

  hasValidSubscriptionSelection() {
    if (!this.isSubscriptionSelected()) return true;

    return Boolean(this.getSelectedSellingPlanId() && this.getMainSellingPlanId());
  }

  getMainSellingPlanId() {
    const sellingPlanId = Number.parseInt(this.dataset.mainSellingPlanId || '', 10);
    return Number.isNaN(sellingPlanId) ? null : sellingPlanId;
  }

  getMainPriceCents() {
    const price = Number(this.currentVariant?.price ?? this.dataset.mainPriceCents ?? 0);
    return Number.isFinite(price) ? price : 0;
  }

  getMainCompareCents() {
    const compareAtPrice = Number(this.currentVariant?.compare_at_price ?? this.dataset.mainCompareCents ?? 0);
    return Number.isFinite(compareAtPrice) ? compareAtPrice : 0;
  }

  getDiscountCents() {
    return Math.max(0, this.getMainPriceCents() - this.getSubscriptionMainPriceCents());
  }

  getDiscountAmountCents() {
    const discount = Number.parseInt(this.dataset.discountAmountCents || this.dataset.discountCents || '0', 10);
    return Number.isNaN(discount) ? 0 : Math.max(0, discount);
  }

  getDiscountPercent() {
    const percent = Number(this.dataset.discountPercent || '0');
    if (!Number.isFinite(percent)) return 0;
    return Math.max(0, Math.min(100, percent));
  }

  getSubscriptionFixedPriceCents() {
    const price = Number.parseInt(this.dataset.subscriptionFixedPriceCents || '0', 10);
    return Number.isNaN(price) ? 0 : Math.max(0, price);
  }

  getEffectiveDiscountPercent() {
    const mainPrice = this.getMainPriceCents();
    const discount = this.getDiscountCents();
    if (mainPrice <= 0 || discount <= 0) return 0;

    return Math.round((discount / mainPrice) * 100);
  }

  getSubscriptionMainPriceCents() {
    const mainPrice = this.getMainPriceCents();
    const mode = this.dataset.subscriptionPriceMode || 'amount';

    if (mode === 'fixed') {
      const fixedPrice = this.getSubscriptionFixedPriceCents();
      if (fixedPrice > 0) return fixedPrice;
    }

    if (mode === 'percent') {
      return Math.max(0, Math.round(mainPrice * (100 - this.getDiscountPercent()) / 100));
    }

    return Math.max(0, mainPrice - this.getDiscountAmountCents());
  }

  getRefillPriceCents() {
    const option = this.getSelectedFrequencyOption();
    const price = Number.parseInt(option?.dataset.refillPriceCents || '0', 10);
    return Number.isNaN(price) ? 0 : price;
  }

  getRefillQuantity() {
    const option = this.getSelectedFrequencyOption();
    const optionQuantity = Number.parseInt(option?.dataset.refillQuantity || '', 10);
    if (!Number.isNaN(optionQuantity) && optionQuantity > 0) return optionQuantity;

    const fallbackQuantity = Number.parseInt(this.dataset.refillQuantity || '1', 10);
    return Number.isNaN(fallbackQuantity) ? 1 : Math.max(1, fallbackQuantity);
  }

  getTodayPriceCents() {
    return this.isSubscriptionSelected() ? this.getSubscriptionMainPriceCents() : this.getMainPriceCents();
  }

  getFrequencyInterval() {
    const option = this.getSelectedFrequencyOption();
    return (option?.dataset.frequencyInterval || option?.textContent || '').trim();
  }

  getFrequencyLabel() {
    const option = this.getSelectedFrequencyOption();
    return (option?.dataset.frequencyLabel || option?.textContent || '').trim();
  }

  syncUI() {
    const subscriptionSelected = this.isSubscriptionSelected();
    const mainPrice = this.getMainPriceCents();
    const mainCompare = this.getMainCompareCents();
    const discount = this.getDiscountCents();
    const subscriptionMainPrice = this.getSubscriptionMainPriceCents();
    const refillPrice = this.getRefillPriceCents();
    const todayPrice = this.getTodayPriceCents();
    const isAvailable = Boolean(this.currentVariant?.available);
    const hasValidSubscription = this.hasValidSubscriptionSelection();
    const disabled = this.isBusy || !isAvailable || !hasValidSubscription;

    this.planCards.forEach((card) => {
      const isSelected = card.dataset.autoRefillPlan === this.selectedPlan;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.frequencySelect instanceof HTMLSelectElement) {
      this.frequencySelect.disabled = this.isBusy || !this.hasSubscriptionOptions();
    }

    if (this.subscriptionSummary instanceof HTMLElement) {
      this.subscriptionSummary.hidden = !subscriptionSelected;
    }

    if (this.subscriptionCurrentPrice instanceof HTMLElement) {
      this.subscriptionCurrentPrice.textContent = this.formatMoney(subscriptionMainPrice);
    } else if (this.subscriptionPrice instanceof HTMLElement) {
      this.subscriptionPrice.textContent = this.formatMoney(subscriptionMainPrice);
    }

    if (this.subscriptionCompare instanceof HTMLElement) {
      const shouldShowSubscriptionCompare = this.dataset.showSubscriptionComparePrice !== 'false' && discount > 0;
      this.subscriptionCompare.hidden = !shouldShowSubscriptionCompare;
      this.subscriptionCompare.textContent = shouldShowSubscriptionCompare ? this.formatMoney(mainPrice) : '';
    }

    if (this.oncePrice instanceof HTMLElement) {
      this.oncePrice.textContent = this.formatMoney(mainPrice);
    }

    if (this.onceCompare instanceof HTMLElement) {
      const shouldShowCompare = mainCompare > mainPrice;
      this.onceCompare.hidden = !shouldShowCompare;
      this.onceCompare.textContent = shouldShowCompare ? this.formatMoney(mainCompare) : '';
    }

    if (this.saveTag instanceof HTMLElement) {
      this.saveTag.hidden = discount <= 0;
      this.saveTag.textContent = this.fillTemplate(this.dataset.saveTemplate || '', {
        amount: this.formatMoney(discount),
        percent: `${this.getEffectiveDiscountPercent()}%`,
        interval: this.getFrequencyInterval(),
        price: this.formatMoney(refillPrice),
        subscription_price: this.formatMoney(subscriptionMainPrice),
        compare_price: this.formatMoney(mainPrice),
      });
    }

    if (this.subscriptionDescription instanceof HTMLElement) {
      this.subscriptionDescription.textContent = this.fillTemplate(this.dataset.subscriptionDescriptionTemplate || '', {
        amount: this.formatMoney(discount),
        percent: `${this.getEffectiveDiscountPercent()}%`,
        interval: this.getFrequencyInterval(),
        price: this.formatMoney(refillPrice),
        subscription_price: this.formatMoney(subscriptionMainPrice),
        compare_price: this.formatMoney(mainPrice),
        plan: this.getFrequencyLabel(),
      });
    }

    if (this.summaryToday instanceof HTMLElement) {
      this.summaryToday.textContent = this.formatMoney(todayPrice);
    }

    if (this.summaryFutureLabel instanceof HTMLElement) {
      this.summaryFutureLabel.textContent = this.fillTemplate(this.dataset.summaryFutureTemplate || '', {
        amount: this.formatMoney(discount),
        percent: `${this.getEffectiveDiscountPercent()}%`,
        interval: this.getFrequencyInterval(),
        price: this.formatMoney(refillPrice),
        subscription_price: this.formatMoney(subscriptionMainPrice),
        compare_price: this.formatMoney(mainPrice),
        plan: this.getFrequencyLabel(),
      });
    }

    if (this.summaryRefill instanceof HTMLElement) {
      this.summaryRefill.textContent = this.formatMoney(refillPrice);
    }

    const ctaText = `${this.dataset.ctaLabel || 'Add to cart'} - ${this.formatMoney(todayPrice)}`;
    if (this.ctaText instanceof HTMLElement) {
      this.ctaText.textContent = ctaText;
    }

    if (this.ctaButton instanceof HTMLButtonElement) {
      this.ctaButton.disabled = disabled;
    }

    if (this.controlsStickyButton && this.stickyButton instanceof HTMLButtonElement) {
      this.stickyButton.disabled = disabled;
      this.stickyButton.textContent = ctaText;
    }
  }

  fillTemplate(template, replacements) {
    return Object.entries(replacements).reduce((value, [key, replacement]) => {
      return value.split(`{${key}}`).join(replacement || '');
    }, template || '');
  }

  async addSelectionToCart({ redirectToCheckout }) {
    if (this.isBusy) return;

    this.currentVariant = this.getVariantById(this.getCurrentVariantId()) || this.getDatasetVariant();

    if (!this.currentVariant?.id || !this.currentVariant.available) {
      this.showError(this.dataset.unavailableText || 'This product is currently unavailable.');
      this.syncUI();
      return;
    }

    if (!this.hasValidSubscriptionSelection()) {
      this.showError(this.dataset.subscriptionUnavailableText || 'Select a valid Auto-Refill plan.');
      this.syncUI();
      return;
    }

    this.clearError();
    this.isBusy = true;
    this.syncUI();

    const isSubscription = this.isSubscriptionSelected();
    const discountCode = (this.dataset.discountCode || '').trim();
    const items = this.buildCartItems();
    const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const requestBody = { items };
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((item) => item.getAttribute('data-section-id') || '')
      .filter(Boolean);

    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || this.dataset.unavailableText || 'Unable to add to cart.');
      }

      let cartUpdateResource = data;

      if (isSubscription && discountCode) {
        await this.applyDiscountCode(discountCode);
        cartUpdateResource = { ...data, sections: {} };
      }

      this.dispatchCartUpdate(cartUpdateResource, totalQuantity);

      if (redirectToCheckout) {
        window.location.href = this.getCheckoutUrl(isSubscription ? discountCode : '');
      } else {
        this.openCartDrawer();
      }
    } catch (error) {
      console.error(error);
      this.showError(error instanceof Error ? error.message : this.dataset.unavailableText || 'Unable to add to cart.');
    } finally {
      this.isBusy = false;
      this.syncUI();
    }
  }

  buildCartItems() {
    const mainItem = {
      id: this.currentVariant.id,
      quantity: 1,
    };

    if (this.isSubscriptionSelected()) {
      const frequencyLabel = this.getFrequencyLabel();
      const mainSellingPlanId = this.getMainSellingPlanId();

      mainItem.selling_plan = mainSellingPlanId;
      mainItem.properties = {
        abbonement: 'annual_refill',
        _auto_refill_frequency: frequencyLabel,
      };
    }

    const selectedAddons = this.getSelectedAddons();
    const linkedAddons = selectedAddons.filter((addon) => addon.linked);
    const linkedAddonGroupId =
      linkedAddons.length > 0 && typeof this.pdpRoot?.createLinkedAddonGroupId === 'function'
        ? this.pdpRoot.createLinkedAddonGroupId()
        : '';

    if (linkedAddonGroupId) {
      mainItem.properties = {
        ...mainItem.properties,
        ...this.pdpRoot.buildLinkedAddonProperties({
          group: linkedAddonGroupId,
          role: 'main',
          removeLinkedRoles: ['addon'],
        }),
      };
    }

    const addonItems = selectedAddons.map((addon) => {
      const item = { id: addon.id, quantity: addon.quantity };

      if (addon.linked && linkedAddonGroupId) {
        item.properties = this.pdpRoot.buildLinkedAddonProperties({ group: linkedAddonGroupId, role: 'addon' });
      }

      return item;
    });

    return [mainItem, ...addonItems];
  }

  getSelectedAddons() {
    return typeof this.pdpRoot?.getSelectedAddons === 'function' ? this.pdpRoot.getSelectedAddons() : [];
  }

  async applyDiscountCode(code) {
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
    } catch (error) {
      console.warn(error);
    }
  }

  dispatchCartUpdate(resource, quantity) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource,
          sourceId: this.id,
          data: {
            source: this.dataset.context || 'auto-refill-buy-block',
            itemCount: quantity,
            variantId: String(this.currentVariant?.id || ''),
            sections: resource.sections || {},
          },
        },
      })
    );
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!(cartDrawer instanceof HTMLElement)) return;

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    if (typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog();
    }
  }

  getCartAddUrl() {
    return this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add.js';
  }

  getCheckoutUrl(discountCode = '') {
    const checkoutUrl = this.dataset.buyNowUrl || '/checkout';
    if (!discountCode) return checkoutUrl;

    const separator = checkoutUrl.includes('?') ? '&' : '?';
    return `${checkoutUrl}${separator}discount=${encodeURIComponent(discountCode)}`;
  }

  formatMoney(cents) {
    const normalizedCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const currency = this.dataset.currencyCode || 'EUR';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: this.dataset.showCurrencyCode === 'true' ? 'code' : 'symbol',
      }).format(normalizedCents / 100);
    } catch (error) {
      return `${(normalizedCents / 100).toFixed(2)} ${currency}`.trim();
    }
  }

  showError(message) {
    if (!(this.errorRegion instanceof HTMLElement)) return;

    this.errorRegion.textContent = message;
    this.errorRegion.hidden = false;
  }

  clearError() {
    if (!(this.errorRegion instanceof HTMLElement)) return;

    this.errorRegion.textContent = '';
    this.errorRegion.hidden = true;
  }
}

if (!customElements.get('auto-refill-buy-block')) {
  customElements.define('auto-refill-buy-block', AutoRefillBuyBlock);
}
